import { Alert, Button, Card, Code, Group, Loader, Stack, Text, Textarea, TextInput, Title } from "@mantine/core";
import { useState } from "react";
import { trpcSlice } from "../../../trpcSlice";

interface IIssueAskAiPreviewResponse {
  rawText: string;
  parsed: unknown | null;
  parseError: string | null;
  prompt: {
    system: string;
    user: string;
  };
}

const appAdminIssueAskAiPreviewApi = trpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    previewIssueAskAi: builder.mutation<
      IIssueAskAiPreviewResponse,
      { projectId: string; issueBoardId: string; question: string }
    >({
      query: (body) => ({
        url: "/appadmin/issue-ask-ai-preview",
        method: "POST",
        body: {
          projectId: body.projectId.trim(),
          issueBoardId: body.issueBoardId.trim(),
          question: body.question.trim(),
        },
      }),
    }),
  }),
});

const { usePreviewIssueAskAiMutation } = appAdminIssueAskAiPreviewApi;

const getErrorMessage = (error: unknown) =>
  (error as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
  (error as { message?: string } | undefined)?.message;

export const IssueAskAiPreviewAdmin = () => {
  const [projectId, setProjectId] = useState("61f4185a553da2419147b655");
  const [issueBoardId, setIssueBoardId] = useState("696dd9b8437ed441592554ad");
  const [question, setQuestion] = useState("Vis åpne saker for RIE med frist passert");
  const [previewIssueAskAi, { data, error, isLoading }] = usePreviewIssueAskAiMutation();

  const canSubmit = projectId.trim().length > 0 && issueBoardId.trim().length > 0 && question.trim().length > 0;
  const errorMessage = getErrorMessage(error);

  return (
    <Card withBorder shadow="sm" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={3}>AI-filterpreview for saker</Title>
          {isLoading && <Loader size="sm" />}
        </Group>

        <Text size="sm" c="dimmed">
          Tester første steg: om et spørsmål kan tolkes som et filter, og hvilket strukturert svar AI-en gir.
        </Text>

        <Group align="flex-end">
          <TextInput
            label="Project ID"
            value={projectId}
            onChange={(event) => setProjectId(event.currentTarget.value)}
            style={{ flex: "1 1 20rem" }}
          />
          <TextInput
            label="Issueboard ID"
            value={issueBoardId}
            onChange={(event) => setIssueBoardId(event.currentTarget.value)}
            style={{ flex: "1 1 20rem" }}
          />
        </Group>

        <Textarea
          label="Spørsmål"
          minRows={3}
          autosize
          value={question}
          onChange={(event) => setQuestion(event.currentTarget.value)}
        />

        <Group>
          <Button
            disabled={!canSubmit || isLoading}
            loading={isLoading}
            onClick={() => previewIssueAskAi({ projectId, issueBoardId, question })}
          >
            Send til AI
          </Button>
        </Group>

        {errorMessage && (
          <Alert color="red" title="Klarte ikke kjøre preview">
            {errorMessage}
          </Alert>
        )}

        {data && (
          <Stack gap="sm">
            {data.parseError && (
              <Alert color="yellow" title="AI-svaret kunne ikke parses">
                {data.parseError}
              </Alert>
            )}

            <Text fw={600}>Parsed</Text>
            <Code block>{JSON.stringify(data.parsed, null, 2)}</Code>

            <Text fw={600}>Raw text</Text>
            <Code block>{data.rawText}</Code>

            <Text fw={600}>System prompt</Text>
            <Code block>{data.prompt.system}</Code>

            <Text fw={600}>User prompt</Text>
            <Code block>{data.prompt.user}</Code>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
